import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeftIcon,
  CheckCircleIcon,
  Cog6ToothIcon,
} from '@heroicons/react/24/outline';
import tenantService from '../../services/tenantService';
import { useToast } from '../../contexts/ToastContext';

const baseConfig = {
  client_label: 'Client',
  savings_enabled: true,
  credit_enabled: true,
  current_accounts_enabled: false,
  term_deposits_enabled: true,
  group_lending_enabled: true,
  individual_lending_enabled: true,
  mandatory_savings_enabled: false,
  collateral_required: false,
  cash_register_required: true,
  multi_agency_enabled: true,
  mobile_money_enabled: true,
  field_agent_enabled: true,
  credit_committee_level: 'agency',
  approval_workflow: 'agent_manager_committee',
  reporting_profile: 'microfinance',
  risk_provisioning_enabled: true,
  member_governance_enabled: false,
};

const emptyTenant = {
  name: '',
  institution_type: 'microfinance',
  description: '',
  contact: '',
  phone: '',
  email: '',
  currency: 'HTG',
  language: 'fr',
  timezone: 'America/Port-au-Prince',
  membership: {
    membership_required: false,
    members_only_services: false,
    membership_fee: 0,
    share_capital_required: false,
    share_unit_value: 0,
    minimum_share_count: 0,
    voting_model: 'none',
    loan_requires_savings: false,
    loan_savings_multiplier: 0,
    require_credit_committee: false,
    board_election_required: false,
    supervisory_committee_required: false,
    allow_dividends: false,
    allow_patronage_refund: false,
  },
  institution_config: baseConfig,
};

const currencies = ['HTG', 'USD', 'EUR', 'XOF', 'XAF', 'DOP', 'CAD'];
const languages = [
  { value: 'fr', label: 'Français' },
  { value: 'ht', label: 'Créole haïtien' },
  { value: 'en', label: 'Anglais' },
  { value: 'es', label: 'Espagnol' },
];
const timezones = [
  { value: 'America/Port-au-Prince', label: 'Haïti - Port-au-Prince' },
  { value: 'America/New_York', label: 'États-Unis - New York' },
  { value: 'America/Panama', label: 'Panama' },
  { value: 'America/Santo_Domingo', label: 'République dominicaine' },
  { value: 'America/Bogota', label: 'Colombie - Bogotá' },
  { value: 'Africa/Dakar', label: 'Sénégal - Dakar' },
  { value: 'Africa/Abidjan', label: "Côte d'Ivoire - Abidjan" },
  { value: 'Africa/Kinshasa', label: 'RDC - Kinshasa' },
  { value: 'UTC', label: 'UTC' },
];

const fallbackInstitutionTypes = [
  { value: 'microfinance', label: 'Micro-finance', defaults: { default_interest_rate: 24, savings_interest_rate: 2.5, minimum_savings_balance: 0, max_loan_amount_multiplier: 3, grace_period_days: 7, config: baseConfig } },
  { value: 'microcredit', label: 'Micro-crédit', defaults: { default_interest_rate: 36, savings_interest_rate: 0, minimum_savings_balance: 0, max_loan_amount_multiplier: 2, grace_period_days: 3, require_credit_committee: true, config: { ...baseConfig, client_label: 'Emprunteur', savings_enabled: false, term_deposits_enabled: false, collateral_required: true, credit_committee_level: 'central', approval_workflow: 'agent_committee', reporting_profile: 'credit_only' } } },
  { value: 'mutual', label: 'Mutuelle', defaults: { default_interest_rate: 18, savings_interest_rate: 3.5, minimum_savings_balance: 100, max_loan_amount_multiplier: 4, grace_period_days: 10, membership_required: true, members_only_services: true, membership_fee: 250, share_capital_required: true, share_unit_value: 500, minimum_share_count: 1, voting_model: 'one_member_one_vote', loan_requires_savings: true, loan_savings_multiplier: 4, require_credit_committee: true, board_election_required: true, supervisory_committee_required: true, allow_dividends: true, allow_patronage_refund: true, config: { ...baseConfig, client_label: 'Membre', group_lending_enabled: false, mandatory_savings_enabled: true, mobile_money_enabled: false, field_agent_enabled: false, credit_committee_level: 'elected_committee', approval_workflow: 'member_committee_board', reporting_profile: 'mutual_membership', member_governance_enabled: true } } },
  { value: 'microbank', label: 'Micro-banque', defaults: { default_interest_rate: 16, savings_interest_rate: 4, minimum_savings_balance: 250, max_loan_amount_multiplier: 5, grace_period_days: 15, loan_requires_savings: true, loan_savings_multiplier: 5, require_credit_committee: true, config: { ...baseConfig, current_accounts_enabled: true, group_lending_enabled: false, mandatory_savings_enabled: false, collateral_required: true, field_agent_enabled: false, credit_committee_level: 'risk_department', approval_workflow: 'branch_risk_management', reporting_profile: 'banking' } } },
  { value: 'cooperative', label: 'Coopérative financière', defaults: { default_interest_rate: 20, savings_interest_rate: 3, minimum_savings_balance: 50, max_loan_amount_multiplier: 4, grace_period_days: 10, membership_required: true, members_only_services: true, membership_fee: 100, share_capital_required: true, share_unit_value: 250, minimum_share_count: 1, voting_model: 'one_member_one_vote', loan_requires_savings: true, loan_savings_multiplier: 4, require_credit_committee: true, board_election_required: true, supervisory_committee_required: true, allow_dividends: true, allow_patronage_refund: true, config: { ...baseConfig, client_label: 'Sociétaire', group_lending_enabled: false, mandatory_savings_enabled: true, mobile_money_enabled: false, field_agent_enabled: false, credit_committee_level: 'elected_committee', approval_workflow: 'member_committee_board', reporting_profile: 'cooperative', member_governance_enabled: true } } },
];

const membershipFromDefaults = (defaults = {}) => ({
  ...emptyTenant.membership,
  membership_required: Boolean(defaults.membership_required),
  members_only_services: Boolean(defaults.members_only_services),
  membership_fee: defaults.membership_fee || 0,
  share_capital_required: Boolean(defaults.share_capital_required),
  share_unit_value: defaults.share_unit_value || 0,
  minimum_share_count: defaults.minimum_share_count || 0,
  voting_model: defaults.voting_model || 'none',
  loan_requires_savings: Boolean(defaults.loan_requires_savings),
  loan_savings_multiplier: defaults.loan_savings_multiplier || 0,
  require_credit_committee: Boolean(defaults.require_credit_committee),
  board_election_required: Boolean(defaults.board_election_required),
  supervisory_committee_required: Boolean(defaults.supervisory_committee_required),
  allow_dividends: Boolean(defaults.allow_dividends),
  allow_patronage_refund: Boolean(defaults.allow_patronage_refund),
});

const normalizeTenant = (tenant) => ({
  name: tenant?.name || '',
  institution_type: tenant?.institution_type || 'microfinance',
  description: tenant?.description || '',
  contact: tenant?.contact || '',
  phone: tenant?.phone || '',
  email: tenant?.email || '',
  currency: tenant?.settings?.general?.currency || tenant?.currency || 'HTG',
  language: tenant?.settings?.general?.language || tenant?.language || 'fr',
  timezone: tenant?.settings?.general?.timezone || tenant?.timezone || 'America/Port-au-Prince',
  membership: { ...emptyTenant.membership, ...(tenant?.settings?.membership || tenant?.membership || {}) },
  institution_config: { ...emptyTenant.institution_config, ...(tenant?.settings?.institution_config || tenant?.institution_config || {}) },
});

const configFromDefaults = (defaults = {}) => ({
  ...emptyTenant.institution_config,
  ...(defaults.config || {}),
});

const GlobalTenantForm = () => {
  const { tenantId } = useParams();
  const isCreateMode = !tenantId;
  const navigate = useNavigate();
  const [institutionTypes, setInstitutionTypes] = useState(fallbackInstitutionTypes);
  const [form, setForm] = useState(emptyTenant);
  const [applyTypeDefaults, setApplyTypeDefaults] = useState(isCreateMode);
  const [loading, setLoading] = useState(!isCreateMode);
  const [saving, setSaving] = useState(false);
  const { showSuccess, showError } = useToast();

  const selectedInstitutionType = useMemo(
    () => institutionTypes.find((type) => type.value === form.institution_type) || institutionTypes[0],
    [institutionTypes, form.institution_type]
  );

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [types, tenants] = await Promise.all([
          tenantService.getInstitutionTypes().catch(() => fallbackInstitutionTypes),
          isCreateMode ? Promise.resolve([]) : tenantService.getTenants(),
        ]);
        setInstitutionTypes(types?.length ? types : fallbackInstitutionTypes);
        if (!isCreateMode) {
          const tenant = tenants.find((item) => String(item.id) === String(tenantId));
          if (!tenant) {
            showError('Tenant introuvable');
            navigate('/global/settings');
            return;
          }
          setForm(normalizeTenant(tenant));
        }
      } catch (error) {
        showError('Erreur lors du chargement du tenant');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [isCreateMode, navigate, showError, tenantId]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleInstitutionTypeChange = (event) => {
    const value = event.target.value;
    const institutionType = institutionTypes.find((type) => type.value === value);
    setForm((prev) => ({
      ...prev,
      institution_type: value,
      membership: membershipFromDefaults(institutionType?.defaults),
      institution_config: configFromDefaults(institutionType?.defaults),
    }));
    setApplyTypeDefaults(true);
  };

  const handleNestedChange = (section) => (event) => {
    const { name, value, type, checked } = event.target;
    setForm((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [name]: type === 'checkbox' ? checked : value,
      },
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!form.name.trim()) {
      showError('Le nom du tenant est requis');
      return;
    }

    try {
      setSaving(true);
      if (isCreateMode) {
        await tenantService.createTenant({ ...form, apply_type_defaults: true });
        showSuccess('Tenant créé avec succès');
      } else {
        await tenantService.updateTenantById(tenantId, {
          ...form,
          apply_type_defaults: applyTypeDefaults,
        });
        showSuccess('Tenant mis à jour avec succès');
      }
      navigate('/global/settings');
    } catch (error) {
      showError(error.response?.data?.message || 'Erreur lors de l’enregistrement');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="text-sm text-gray-500">Chargement...</div>;
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <Link to="/global/settings" className="inline-flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-gray-900">
            <ArrowLeftIcon className="h-4 w-4" />
            Retour à la liste
          </Link>
          <h1 className="mt-2 text-2xl font-semibold text-gray-900">{isCreateMode ? 'Créer un tenant' : 'Configurer le tenant'}</h1>
          <p className="mt-1 text-sm text-gray-600">Profil institutionnel, règles métier et paramètres généraux.</p>
        </div>
        <button type="submit" disabled={saving} className="btn-primary inline-flex items-center gap-2">
          <CheckCircleIcon className="h-5 w-5" />
          {saving ? 'Enregistrement...' : 'Enregistrer'}
        </button>
      </div>

      <section className="bg-white border border-gray-200 rounded-lg shadow-sm">
        <div className="px-5 py-4 border-b border-gray-200 flex items-center gap-2">
          <Cog6ToothIcon className="h-5 w-5 text-gray-500" />
          <h2 className="text-sm font-semibold text-gray-900">Identification et type</h2>
        </div>
        <div className="p-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <label className="sm:col-span-2">
            <span className="block text-sm font-medium text-gray-700">Nom du tenant</span>
            <input name="name" value={form.name} onChange={handleChange} className="input-field mt-1" required />
          </label>
          <label className="sm:col-span-2">
            <span className="block text-sm font-medium text-gray-700">Type d’institution</span>
            <select name="institution_type" value={form.institution_type} onChange={handleInstitutionTypeChange} className="input-field mt-1">
              {institutionTypes.map((type) => <option key={type.value} value={type.value}>{type.label}</option>)}
            </select>
          </label>
          <div className="sm:col-span-2 rounded-lg border border-blue-100 bg-blue-50 p-4">
            <div className="text-sm font-medium text-blue-900">Configuration proposée: {selectedInstitutionType?.label}</div>
            <div className="mt-3 grid grid-cols-2 gap-3 text-sm text-blue-900 sm:grid-cols-3">
              <div><span className="block text-blue-600">Taux prêt</span>{selectedInstitutionType?.defaults?.default_interest_rate}%</div>
              <div><span className="block text-blue-600">Taux épargne</span>{selectedInstitutionType?.defaults?.savings_interest_rate}%</div>
              <div><span className="block text-blue-600">Solde minimum</span>{selectedInstitutionType?.defaults?.minimum_savings_balance}</div>
              <div><span className="block text-blue-600">Multiplicateur prêt</span>{selectedInstitutionType?.defaults?.max_loan_amount_multiplier}x</div>
              <div><span className="block text-blue-600">Grâce</span>{selectedInstitutionType?.defaults?.grace_period_days} jours</div>
              <div><span className="block text-blue-600">Rapports</span>{selectedInstitutionType?.defaults?.config?.reporting_profile || '-'}</div>
            </div>
            {!isCreateMode && (
              <label className="mt-3 flex items-center gap-2 text-sm text-blue-900">
                <input
                  type="checkbox"
                  checked={applyTypeDefaults}
                  onChange={(event) => setApplyTypeDefaults(event.target.checked)}
                  className="h-4 w-4 rounded border-blue-300 text-primary-600 focus:ring-primary-500"
                />
                Réappliquer les paramètres par défaut de ce type
              </label>
            )}
          </div>
          <label className="sm:col-span-2">
            <span className="block text-sm font-medium text-gray-700">Description</span>
            <textarea name="description" value={form.description} onChange={handleChange} className="input-field mt-1 min-h-[84px]" />
          </label>
          <label>
            <span className="block text-sm font-medium text-gray-700">Contact</span>
            <input name="contact" value={form.contact} onChange={handleChange} className="input-field mt-1" />
          </label>
          <label>
            <span className="block text-sm font-medium text-gray-700">Téléphone</span>
            <input name="phone" value={form.phone} onChange={handleChange} className="input-field mt-1" />
          </label>
          <label className="sm:col-span-2">
            <span className="block text-sm font-medium text-gray-700">Email</span>
            <input name="email" type="email" value={form.email} onChange={handleChange} className="input-field mt-1" />
          </label>
          <label>
            <span className="block text-sm font-medium text-gray-700">Devise</span>
            <select name="currency" value={form.currency} onChange={handleChange} className="input-field mt-1">
              {currencies.map((currency) => <option key={currency} value={currency}>{currency}</option>)}
            </select>
          </label>
          <label>
            <span className="block text-sm font-medium text-gray-700">Langue</span>
            <select name="language" value={form.language} onChange={handleChange} className="input-field mt-1">
              {languages.map((language) => <option key={language.value} value={language.value}>{language.label}</option>)}
            </select>
          </label>
          <label className="sm:col-span-2">
            <span className="block text-sm font-medium text-gray-700">Fuseau horaire</span>
            <select name="timezone" value={form.timezone} onChange={handleChange} className="input-field mt-1">
              {timezones.map((timezone) => <option key={timezone.value} value={timezone.value}>{timezone.label}</option>)}
            </select>
          </label>
        </div>
      </section>

      <section className="bg-white border border-gray-200 rounded-lg shadow-sm">
        <div className="px-5 py-4 border-b border-gray-200">
          <h2 className="text-sm font-semibold text-gray-900">Configuration de fonctionnement</h2>
        </div>
        <div className="p-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <label>
            <span className="block text-sm font-medium text-gray-700">Libellé des personnes</span>
            <input name="client_label" value={form.institution_config.client_label} onChange={handleNestedChange('institution_config')} className="input-field mt-1" />
          </label>
          <label>
            <span className="block text-sm font-medium text-gray-700">Niveau comité crédit</span>
            <select name="credit_committee_level" value={form.institution_config.credit_committee_level} onChange={handleNestedChange('institution_config')} className="input-field mt-1">
              <option value="none">Aucun</option>
              <option value="agency">Agence</option>
              <option value="central">Central</option>
              <option value="elected_committee">Comité élu</option>
              <option value="risk_department">Département risque</option>
            </select>
          </label>
          <label>
            <span className="block text-sm font-medium text-gray-700">Workflow approbation</span>
            <select name="approval_workflow" value={form.institution_config.approval_workflow} onChange={handleNestedChange('institution_config')} className="input-field mt-1">
              <option value="agent_manager_committee">Agent -> Manager -> Comité</option>
              <option value="agent_committee">Agent -> Comité</option>
              <option value="member_committee_board">Membre -> Comité -> Conseil</option>
              <option value="branch_risk_management">Agence -> Risque -> Direction</option>
            </select>
          </label>
          <label>
            <span className="block text-sm font-medium text-gray-700">Profil rapports</span>
            <select name="reporting_profile" value={form.institution_config.reporting_profile} onChange={handleNestedChange('institution_config')} className="input-field mt-1">
              <option value="microfinance">Micro-finance</option>
              <option value="credit_only">Crédit seulement</option>
              <option value="mutual_membership">Mutuelle / membres</option>
              <option value="banking">Bancaire</option>
              <option value="cooperative">Coopérative</option>
            </select>
          </label>
          {[
            ['savings_enabled', 'Épargne activée'],
            ['credit_enabled', 'Crédit activé'],
            ['current_accounts_enabled', 'Comptes courants'],
            ['term_deposits_enabled', 'Dépôts à terme'],
            ['group_lending_enabled', 'Crédit de groupe'],
            ['individual_lending_enabled', 'Crédit individuel'],
            ['mandatory_savings_enabled', 'Épargne obligatoire'],
            ['collateral_required', 'Garantie obligatoire'],
            ['cash_register_required', 'Caisse obligatoire'],
            ['multi_agency_enabled', 'Multi-agence'],
            ['mobile_money_enabled', 'Mobile money'],
            ['field_agent_enabled', 'Agents de terrain'],
            ['risk_provisioning_enabled', 'Provisionnement risque'],
            ['member_governance_enabled', 'Gouvernance par membres'],
          ].map(([name, label]) => (
            <label key={name} className="flex items-center gap-3 rounded-md border border-gray-200 px-3 py-2">
              <input
                type="checkbox"
                name={name}
                checked={Boolean(form.institution_config?.[name])}
                onChange={handleNestedChange('institution_config')}
                className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <span className="text-sm text-gray-700">{label}</span>
            </label>
          ))}
        </div>
      </section>

      <section className="bg-white border border-gray-200 rounded-lg shadow-sm">
        <div className="px-5 py-4 border-b border-gray-200">
          <h2 className="text-sm font-semibold text-gray-900">Règles Mutuelle / Coopérative</h2>
        </div>
        <div className="p-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
          {[
            ['membership_required', 'Adhésion obligatoire'],
            ['members_only_services', 'Services réservés aux membres'],
            ['share_capital_required', 'Parts sociales obligatoires'],
            ['loan_requires_savings', 'Crédit conditionné par l’épargne'],
            ['require_credit_committee', 'Comité de crédit requis'],
            ['board_election_required', 'Élection du conseil requise'],
            ['supervisory_committee_required', 'Comité de surveillance requis'],
            ['allow_dividends', 'Dividendes autorisés'],
            ['allow_patronage_refund', 'Ristournes autorisées'],
          ].map(([name, label]) => (
            <label key={name} className="flex items-center gap-3 rounded-md border border-gray-200 px-3 py-2">
              <input
                type="checkbox"
                name={name}
                checked={Boolean(form.membership?.[name])}
                onChange={handleNestedChange('membership')}
                className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <span className="text-sm text-gray-700">{label}</span>
            </label>
          ))}
          <label>
            <span className="block text-sm font-medium text-gray-700">Frais d’adhésion</span>
            <input name="membership_fee" type="number" min="0" step="0.01" value={form.membership.membership_fee} onChange={handleNestedChange('membership')} className="input-field mt-1" />
          </label>
          <label>
            <span className="block text-sm font-medium text-gray-700">Valeur d’une part sociale</span>
            <input name="share_unit_value" type="number" min="0" step="0.01" value={form.membership.share_unit_value} onChange={handleNestedChange('membership')} className="input-field mt-1" />
          </label>
          <label>
            <span className="block text-sm font-medium text-gray-700">Nombre minimum de parts</span>
            <input name="minimum_share_count" type="number" min="0" step="1" value={form.membership.minimum_share_count} onChange={handleNestedChange('membership')} className="input-field mt-1" />
          </label>
          <label>
            <span className="block text-sm font-medium text-gray-700">Multiplicateur prêt/épargne</span>
            <input name="loan_savings_multiplier" type="number" min="0" step="0.01" value={form.membership.loan_savings_multiplier} onChange={handleNestedChange('membership')} className="input-field mt-1" />
          </label>
          <label className="sm:col-span-2">
            <span className="block text-sm font-medium text-gray-700">Modèle de vote</span>
            <select name="voting_model" value={form.membership.voting_model} onChange={handleNestedChange('membership')} className="input-field mt-1">
              <option value="none">Non applicable</option>
              <option value="one_member_one_vote">Un membre = une voix</option>
              <option value="shares_weighted">Pondéré par parts sociales</option>
            </select>
          </label>
        </div>
      </section>

      <div className="flex justify-end gap-3">
        <Link to="/global/settings" className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">
          Annuler
        </Link>
        <button type="submit" disabled={saving} className="btn-primary inline-flex items-center gap-2">
          <CheckCircleIcon className="h-5 w-5" />
          {saving ? 'Enregistrement...' : 'Enregistrer'}
        </button>
      </div>
    </form>
  );
};

export default GlobalTenantForm;
